import { Box, Button, ButtonGroup, createListCollection, For, HStack, Text, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import GridLayout from '@components/cockpit/panels/operatorStationConfiguration/context/GridLayout';
import { CustomSelect } from '@components/ui/select/Select';
import { useAppSelector } from '@hooks/redux.hooks';
import { LayoutSelectors, selectActiveGlobalScreenConfiguration } from '@redux/config/config.selectors';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ContextEnum,
  GlobalScreenConfigurationWithNullableCoordinates,
} from '@/types/config/screenConfiguration.types';

export function ScreenContextAssignmentForm() {
  const { formatMessage } = useIntl();

  const { values } = useFormikContext<Partial<GlobalScreenConfigurationWithNullableCoordinates>>();

  const gridLayouts = useAppSelector(LayoutSelectors.selectAllLayouts);
  const selectedConfig = useAppSelector(selectActiveGlobalScreenConfiguration);

  const [selectedContext, setSelectedContext] = useState<string>(ContextEnum.LIVE);

  const items = createListCollection({
    items: gridLayouts.map((grid) => ({ label: grid.name, value: grid.code })),
  });

  return (
    <CockpitFormLayout
      title="components.operatorStationConfiguration.context.assignScreensToContexts.title"
      width="100%"
    >
      <ButtonGroup gap={1}>
        <For
          each={Object.values(ContextEnum).filter((context) =>
            selectedConfig?.screenConfigurations.some((sc) => context === sc.context),
          )}
        >
          {(context) => (
            <Button
              key={context}
              variant="toggle"
              size="toggleM"
              width="fit-content"
              data-active={context === selectedContext}
              onClick={() => setSelectedContext(context)}
            >
              <FormattedMessage id={`screenConfiguration.context.${context}`} />
            </Button>
          )}
        </For>
      </ButtonGroup>
      {selectedConfig && (
        <HStack
          gap={3}
          padding={3}
          width="100%"
          minHeight="0"
          flex="0 0 auto"
          justifyContent="center"
          alignItems="stretch"
          backgroundColor="neutral.850"
        >
          <FieldArray name="screenConfigurations">
            {({ replace }) =>
              values.screenConfigurations &&
              values.screenConfigurations.length > 0 &&
              values.screenConfigurations
                .filter((config) => config.context === selectedContext)
                .toSorted((config1, config2) => Number(config1.code) - Number(config2.code))
                .map((screenConfig) => {
                  const gridLayout = gridLayouts.find((grid) => grid.code === screenConfig.gridLayoutCode);

                  const realIndex = values.screenConfigurations!.findIndex(
                    (cfg) => cfg.context === screenConfig.context && cfg.code === screenConfig.code,
                  );

                  return (
                    <VStack flex={1} key={screenConfig.code} height="100%" alignItems="stretch" gap={2}>
                      <Text color="neutral.300" alignSelf="flex-start">
                        <FormattedMessage
                          id="components.operatorStationConfiguration.context.assignScreensToContexts.screenLabel"
                          values={{ isLeftScreen: screenConfig.code === '1' }}
                        />
                      </Text>
                      <GridLayout gridLayoutCode={screenConfig.gridLayoutCode} />
                      <Box width="60%" height="56px" flexShrink={0} alignSelf="flex-start">
                        <Field
                          as={CustomSelect}
                          name={`screenConfig.${realIndex}.gridLayoutCode`}
                          key={`${selectedContext}-${screenConfig.code}`}
                          value={gridLayout?.code}
                          collection={items}
                          placeholder={formatMessage({ id: 'screenConfiguration.gridPlaceholder' })}
                          onChange={(value: string) => replace(realIndex, { ...screenConfig, gridLayoutCode: value })}
                        />
                      </Box>
                    </VStack>
                  );
                })
            }
          </FieldArray>
        </HStack>
      )}
    </CockpitFormLayout>
  );
}
